import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ShoppingBag } from "lucide-react";
import { BRAND, CHECKOUT_URL } from "../lib/brand";

const links = [
  { to: "/", label: "Home", id: "home" },
  { to: "/about", label: "About", id: "about" },
  { to: "/shop", label: "Shop", id: "product" },
  { to: "/community", label: "Community", id: "community" },
];

export const Nav = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [pathname]);

  return (
    <motion.header
      data-testid="nav-header"
      initial={{ y: -80 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1], delay: 0.2 }}
      className={`fixed top-0 left-0 right-0 z-50 bg-white transition-all duration-300 ${
        scrolled ? "border-b border-black/10 py-2 shadow-sm" : "py-3"
      }`}
    >
      <nav className="mx-auto flex max-w-[1400px] items-center justify-between px-5 sm:px-8">
        <Link to="/" data-testid="nav-logo" className="flex items-center gap-2 shrink-0">
          <img src={BRAND.logo} alt="Featherbomb" className={`object-contain transition-all duration-300 ${scrolled ? "h-11 w-11" : "h-14 w-14"}`} />
        </Link>

        <div className="hidden items-center gap-9 md:flex">
          {links.map((l) => (
            <Link
              key={l.id}
              to={l.to}
              data-testid={`nav-link-${l.id}`}
              className={`link-underline text-sm font-semibold uppercase tracking-wider ${
                pathname === l.to ? "text-[#EE4026]" : "text-[#212121]"
              }`}
            >
              {l.label}
            </Link>
          ))}
        </div>

        <div className="flex items-center gap-3">
          <a
            href={CHECKOUT_URL}
            target="_blank"
            rel="noreferrer"
            data-testid="nav-buy-button"
            className="btn-primary hidden items-center gap-2 rounded-full px-5 py-2.5 text-sm font-bold uppercase tracking-wide sm:flex"
          >
            <ShoppingBag size={16} />
            Order Now
          </a>
          <button
            data-testid="nav-mobile-toggle"
            onClick={() => setOpen((v) => !v)}
            className="grid h-11 w-11 place-items-center rounded-full bg-[#212121] text-white md:hidden"
            aria-label="Menu"
          >
            {open ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>
      </nav>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="overflow-hidden glass md:hidden"
          >
            <div className="flex flex-col gap-1 px-5 py-4">
              {links.map((l) => (
                <Link
                  key={l.id}
                  to={l.to}
                  data-testid={`nav-mobile-link-${l.id}`}
                  className="rounded-xl px-4 py-3 text-lg font-bold uppercase tracking-wide text-[#212121] hover:bg-black/5"
                >
                  {l.label}
                </Link>
              ))}
              <a
                href={CHECKOUT_URL}
                target="_blank"
                rel="noreferrer"
                className="btn-primary mt-2 rounded-full px-5 py-3 text-center text-sm font-bold uppercase tracking-wide"
              >
                Order Featherbomb
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
};
